import React, { useMemo } from 'react';
import type { DailyRecord } from '@skd/shared';
import { INSPECTION_STAGES, INSPECTION_ITEMS, isItemApplicable } from '@skd/shared';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useToast } from '../../hooks/useToast';

interface WhatsAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  records: DailyRecord[];
}

export function WhatsAppModal({ isOpen, onClose, records }: WhatsAppModalProps) {
  const { showToast } = useToast();

  const message = useMemo(() => {
    if (records.length === 0) return 'No records available.';

    let msg = 'SKD BODY QUALITY AUDIT SUMMARY\n';
    msg += '══════════════════════════\n\n';

    let totalChecked = 0;
    let totalOK = 0;
    let totalNG = 0;

    for (const record of records) {
      msg += `Body: ${record.metadata.body_number}\n`;
      msg += `Model: ${record.metadata.model} | Invoice: ${record.metadata.invoice_no}\n`;

      for (const stage of INSPECTION_STAGES) {
        const stageData = record.stages[stage.id];
        if (!stageData) continue;
        const savedInfo = stageData.saved ? ` (${stageData.savedBy} - ${stageData.savedAt})` : ' (not saved)';
        const ngItems: string[] = [];

        for (const item of INSPECTION_ITEMS) {
          if (!isItemApplicable(item.id, stage.id)) continue;
          const itemData = stageData.items[item.id];
          if (!itemData) continue;
          totalChecked++;
          if (itemData.status === 'OK') totalOK++;
          else if (itemData.status === 'NG') {
            totalNG++;
            ngItems.push(`${item.code}(${itemData.defectType})`);
          }
        }

        msg += `  ${stage.shortName}: ${stageData.actionStatus}${savedInfo}`;
        if (ngItems.length > 0) msg += ` [${ngItems.join(', ')}]`;
        msg += '\n';
      }

      msg += `Verdict: ${record.verdict}\n`;
      msg += `Pass Rate: ${record.passRate}%\n\n`;
    }

    const overallRate = totalChecked > 0 ? Math.round((totalOK / totalChecked) * 100) : 0;
    msg += `OVERALL: ${totalOK}/${totalChecked} OK | ${totalNG} NG | ${overallRate}% Pass Rate\n`;

    return msg;
  }, [records]);

  const copyMessage = () => {
    navigator.clipboard.writeText(message).then(() => {
      showToast('WhatsApp summary copied to clipboard!');
    }).catch(() => {
      showToast('Failed to copy message');
    });
  };

  return (
    <Modal id="whatsapp" isOpen={isOpen} onClose={onClose} title="WhatsApp Group Summary" size="md">
      <div className="space-y-4">
        <textarea
          className="input-field font-mono text-xs h-64 resize-none"
          value={message}
          readOnly
        />
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button
            variant="primary"
            onClick={copyMessage}
            className="!bg-[#25D366] !text-white !border-[#25D366] hover:!bg-[#20bd5a]"
          >
            Copy WhatsApp Message
          </Button>
        </div>
      </div>
    </Modal>
  );
}
